import { Field, Input, Select, Tabs, Textarea } from '@bb/ui';
import { useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { incompleteByLocale } from './TranslationIncompleteNotice.tsx';
import { TranslationStatusBadge, TRANSLATION_STATUS_LABELS } from './TranslationStatusBadge.tsx';
import {
  emptyFields,
  environmentLocales,
  humanizePath,
  isRtlLocale,
  previewContent,
  readPath,
  setTranslation,
  setTranslationStatus,
  textFieldPaths,
  translationOf,
  translationStatusOf,
  TRANSLATION_STATUSES,
  writePath,
  type ChannelContent,
  type LocaleSettings,
  type TranslationStatus,
} from './localization.ts';

export interface LocaleTabsProps {
  /** The environment's locales: `localeSettingsOf(environment)` or `useEnvironmentLocales(environmentId)`. */
  locales: LocaleSettings;
  /** One channel content (e.g. `variant.content.push_android`): the default fields plus `localized` and `translationStatus`. */
  value: ChannelContent;
  /** Every edit, with the whole content object. */
  onChange: (next: ChannelContent) => void;
  /** Text fields to translate, as dot paths of the default content (`title`, `card.title`); by default every text field (`textFieldPaths`). */
  fields?: readonly string[];
  /** Field labels by path; by default the path in words. */
  labels?: Readonly<Record<string, string>>;
  /** The channel composer's own editor for the default tab; by default one text box per field. */
  renderDefault?: () => ReactNode;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
  /** The last failed publish (or save): a 422 `translation_incomplete` is shown in the tabs of the locales it names. */
  saveError?: unknown;
  /** Which entries of `saveError` are about this content: the variant API id and channel; every entry by default. */
  scope?: { variant: string; channel: string };
  /** Accessible name of the tab list. */
  label?: string;
}

const STATUS_OPTIONS = TRANSLATION_STATUSES.map((s) => ({ value: s, label: TRANSLATION_STATUS_LABELS[s] }));
const STATUS_HINTS: Record<TranslationStatus, string> = {
  missing: 'Not sent: users of this locale get its fallback, whatever is typed here.',
  draft: 'Sent as it is while it is reviewed.',
  ready: 'Reviewed: publishing needs every field filled.',
};

const isLong = (text: string) => text.length > 80 || text.includes('\n');

/** A text box that grows into a textarea for long texts; Field wires its id and descriptions. */
function TextBox({ multiline, value, onText, ...rest }: { multiline: boolean; value: string; onText: (text: string) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  if (multiline) return <Textarea {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)} value={value} rows={3} onChange={(e) => onText(e.target.value)} />;
  return <Input {...rest} value={value} onChange={(e) => onText(e.target.value)} />;
}

function LocalePanel({
  locale,
  value,
  onChange,
  paths,
  labelOf,
  locales,
  readOnly,
  refused,
}: {
  locale: string;
  value: ChannelContent;
  onChange: (next: ChannelContent) => void;
  paths: readonly string[];
  labelOf: (path: string) => string;
  locales: LocaleSettings;
  readOnly: boolean;
  refused: readonly string[];
}) {
  const { defaultLocale } = environmentLocales(locales);
  const status = translationStatusOf(value, locale);
  const source = previewContent(value, locale, locales).locale;
  const stillEmpty = status === 'ready' ? refused.filter((p) => !translationOf(value, locale, p).trim()) : [];
  const rtl = isRtlLocale(locale);
  return (
    <div className="stack-form" role="group" aria-label={`Translation: ${locale}`}>
      <Field label="Translation status" hint={STATUS_HINTS[status]}>
        <Select options={STATUS_OPTIONS} value={status} disabled={readOnly} onChange={(e) => onChange(setTranslationStatus(value, locale, e.target.value as TranslationStatus))} />
      </Field>
      {source !== locale && (
        <p className="hint">
          Users with {locale} get {source === defaultLocale ? `the default (${defaultLocale}) text` : `the ${source} translation`} until this one is filled in.
        </p>
      )}
      {stillEmpty.length > 0 && (
        <p className="bb-field__error" role="alert">
          Publishing was refused: {stillEmpty.map(labelOf).join(', ')} {stillEmpty.length === 1 ? 'is' : 'are'} empty in this ready translation.
        </p>
      )}
      {paths.map((path) => {
        const fallback = readPath(value, path);
        const text = translationOf(value, locale, path);
        return (
          <Field key={path} label={labelOf(path)} hint={`Empty: the default (${defaultLocale}) text is shown.`} error={status === 'ready' && !text.trim() ? 'A ready translation needs this field.' : undefined}>
            <TextBox
              multiline={isLong(typeof fallback === 'string' ? fallback : '')}
              value={text}
              placeholder={typeof fallback === 'string' ? fallback : ''}
              lang={locale}
              dir={rtl ? 'rtl' : undefined}
              disabled={readOnly}
              onText={(next) => onChange(setTranslation(value, locale, path, next))}
            />
          </Field>
        );
      })}
    </div>
  );
}

/**
 * Locales of one channel content (BB-55): «Default» edits the default fields (the channel composer's editor, or a
 * text box per field), and a tab per environment locale edits its translation as overrides over the default — an
 * empty field falls back to the default text — with the translation status. Each tab shows its status badge; a
 * `ready` locale with empty fields is flagged here before publishing refuses it.
 */
export function LocaleTabs({ locales, value, onChange, fields, labels = {}, renderDefault, readOnly = false, saveError, scope, label = 'Locales' }: LocaleTabsProps) {
  const { defaultLocale, codes } = environmentLocales(locales);
  const paths = fields ?? textFieldPaths(value);
  const refused = incompleteByLocale(saveError, scope);
  const [tab, setTab] = useState('default');
  const current = codes.includes(tab) && tab !== defaultLocale ? tab : 'default';
  const labelOf = (path: string) => labels[path] ?? humanizePath(path);
  const defaultText = (path: string) => {
    const v = readPath(value, path);
    return typeof v === 'string' ? v : '';
  };

  const defaultTab = renderDefault ? (
    renderDefault()
  ) : (
    <div className="stack-form" role="group" aria-label={`Default content: ${defaultLocale}`}>
      {paths.map((path) => (
        <Field key={path} label={labelOf(path)}>
          <TextBox multiline={isLong(defaultText(path))} value={defaultText(path)} lang={defaultLocale} disabled={readOnly} onText={(text) => onChange(writePath(value, path, text))} />
        </Field>
      ))}
    </div>
  );

  const items = [
    { value: 'default', label: `Default (${defaultLocale})`, content: defaultTab },
    ...codes.slice(1).map((locale) => {
      const status = translationStatusOf(value, locale);
      const incomplete = status === 'ready' && emptyFields(value, locale, paths).length > 0;
      return {
        value: locale,
        label: (
          <>
            {locale} <TranslationStatusBadge status={status} incomplete={incomplete} />
          </>
        ),
        content: <LocalePanel locale={locale} value={value} onChange={onChange} paths={paths} labelOf={labelOf} locales={locales} readOnly={readOnly} refused={refused[locale] ?? []} />,
      };
    }),
  ];

  return (
    <section className="locale-tabs" aria-label={label}>
      <Tabs label={label} items={items} value={current} onValueChange={setTab} />
      {codes.length === 1 && <p className="hint">The environment has one locale. Add locales in Settings → Locales to translate this message.</p>}
    </section>
  );
}
